import React, { useCallback, useMemo, useEffect } from 'react'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import TileCard from 'tripPlanner/components/EntryPoints/Common/Tile/TileCard'
import LineBalanceScaleIcon from 'LuxKit/Icons/line/LineBalanceScaleIcon'
import LineCompassIcon from 'LuxKit/Icons/line/LineCompassIcon'
import LineWalkingIcon from 'LuxKit/Icons/line/LineWalkingIcon'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TileCardsGroup from './Common/Tile/TileCardsGroup'
import { arrayToMap } from 'lib/array/arrayUtils'
import * as Analytics from 'analytics/analytics'

export const PROXIMITY = [
  {
    proximity: 'Keep it local',
    title: 'Keep it local',
    description: 'Low travel time, high relaxation.',
    icon: <LineWalkingIcon />,
  },
  {
    proximity: 'See the best within reach',
    title: 'Balanced',
    description: 'The ideal mix of local and discovery.',
    icon: <LineBalanceScaleIcon />,
  },
  {
    proximity: 'Keen to get out and about',
    title: 'Go all out',
    description: 'Stretch the map, make it count.',
    icon: <LineCompassIcon />,
  },
]

interface Props {
  proximity: Array<{
    proximity: string
    selected: boolean
  }>
  onProximitySelect: (proximity: string) => void
  onNext: () => void
  isGeneratingTrip: boolean
  stepNumber: number
  totalSteps: number
}

function AITripProximitySelection({
  proximity,
  onProximitySelect,
  onNext,
  isGeneratingTrip,
  stepNumber,
  totalSteps,
}: Props) {
  const handleOnClick = useCallback((proximityValue: string) => {
    onProximitySelect(proximityValue)
    onNext()
  }, [onProximitySelect, onNext])

  const tripProximityMap = useMemo(() =>
    arrayToMap(proximity, proximityOption => proximityOption.proximity, proximityOption => proximityOption.selected),
  [proximity])

  useEffect(() => {
    Analytics.trackClientEvent({
      subject: 'travel_distance',
      action: 'view',
      category: 'trip_planner',
      type: 'nonInteraction',
    })
  }, [])

  return (
    <Group direction="vertical" gap={20} desktopGap={24}>
      <VerticalSpacer gap={8}>
        <BodyText variant="large" weight="bold">{stepNumber} of {totalSteps}</BodyText>
        <Heading variant="heading1">
          Travel <i>Distance</i>
        </Heading>
        <BodyText variant="large">
          How far are you happy to venture from your hotel?
        </BodyText>
      </VerticalSpacer>
      <TileCardsGroup>
        {PROXIMITY.map(proximityOption => (
          <TileCard
            key={proximityOption.proximity}
            title={proximityOption.title}
            subtitle={proximityOption.description}
            icon={proximityOption.icon}
            value={proximityOption.proximity}
            onClick={() => handleOnClick(proximityOption.proximity)}
            selected={!!tripProximityMap.get(proximityOption.proximity)}
            disabled={isGeneratingTrip}
          />))}
      </TileCardsGroup>
    </Group>
  )
}

export default AITripProximitySelection
